import { VISIBILITY_LEVELS, type CalendarPatch, type CalendarView, type ConnectionView, type Provider, type ProvidersStatus } from "@confluence/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { del, get, patch, post } from "../api";
import { useI18n, type Translate } from "../i18n";
import { Alert, Field, useForm } from "../ui";

const PROVIDER_NAMES: Record<Provider, string> = { google: "Google", microsoft: "Microsoft 365", caldav: "CalDAV", ics: "iCal" };
const PROVIDER_BADGE: Record<Provider, string> = { google: "G", microsoft: "M", caldav: "C", ics: "ICS" };

export function useConnections() {
  return useQuery({
    queryKey: ["connections"],
    queryFn: async () => (await get<{ connections: ConnectionView[] }>("/api/connections")).connections,
    // Tant qu'une synchronisation tourne, on rafraîchit régulièrement.
    refetchInterval: (q) => (q.state.data?.some((c) => c.syncing || !c.lastSyncedAt) ? 2500 : 60_000),
  });
}

export function relativeTime(iso: string, locale: string): string {
  const diff = (new Date(iso).getTime() - Date.now()) / 1000;
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  const abs = Math.abs(diff);
  if (abs < 60) return rtf.format(Math.round(diff), "second");
  if (abs < 3600) return rtf.format(Math.round(diff / 60), "minute");
  if (abs < 86400) return rtf.format(Math.round(diff / 3600), "hour");
  return rtf.format(Math.round(diff / 86400), "day");
}

function statusLine(c: ConnectionView, t: Translate, locale: string) {
  if (c.syncing) return <span className="muted">{t("cal.status.syncing")}</span>;
  if (c.status === "reauth") return <span className="warn-text">{t("cal.status.reauth")}</span>;
  if (c.status === "error") return <span className="warn-text">{t("cal.status.error", { error: c.lastError ?? "" })}</span>;
  if (!c.lastSyncedAt) return <span className="muted">{t("cal.status.never")}</span>;
  return <span className="muted">{t("cal.status.ok", { when: relativeTime(c.lastSyncedAt, locale) })}</span>;
}

export function CalendarsSection() {
  const { t } = useI18n();
  const connections = useConnections();
  const params = useRef(new URLSearchParams(window.location.search)).current;
  const connected = params.get("connected") as Provider | null;
  const oauthError = params.get("oauthError");
  const [added, setAdded] = useState(false);

  useEffect(() => {
    // Nettoie l'adresse après un retour OAuth, pour ne pas réafficher le message au rechargement.
    if (connected || oauthError) window.history.replaceState(null, "", "/settings?section=calendars");
  }, []);

  return (
    <section className="stack">
      <h2>{t("settings.calendars")}</h2>
      <p className="muted measure">{t("cal.lead")}</p>
      {connected && <Alert kind="ok">{t("cal.connected", { provider: PROVIDER_NAMES[connected] })}</Alert>}
      {added && <Alert kind="ok">{t("cal.added")}</Alert>}
      {oauthError && <OAuthError code={oauthError} />}
      {connections.data?.length === 0 && <p className="muted">{t("cal.empty")}</p>}
      {connections.data?.map((c) => <ConnectionCard key={c.id} conn={c} />)}
      <AddAccount onAdded={() => setAdded(true)} />
    </section>
  );
}

function OAuthError({ code }: { code: string }) {
  const { t } = useI18n();
  const key = (["not_configured", "cancelled", "expired", "failed", "admin_consent"].includes(code) ? code : "failed") as
    | "not_configured"
    | "cancelled"
    | "expired"
    | "failed"
    | "admin_consent";
  return (
    <Alert kind={key === "cancelled" ? "info" : "error"}>
      {t(`oauth.${key}`)}
      {key === "admin_consent" && <AdminConsentLink />}
    </Alert>
  );
}

function AdminConsentLink() {
  // L'administrateur Microsoft 365 de l'organisation approuve l'application pour tous ses utilisateurs.
  const redirect = `${window.location.origin}/api/oauth/microsoft/callback`;
  const settings = useQuery({ queryKey: ["ms-client"], queryFn: () => get<{ clientId: string | null }>("/api/oauth/microsoft/client") });
  if (!settings.data?.clientId) return null;
  const url = `https://login.microsoftonline.com/organizations/adminconsent?client_id=${encodeURIComponent(settings.data.clientId)}&redirect_uri=${encodeURIComponent(redirect)}`;
  return (
    <code className="copy" style={{ display: "block", marginTop: 8 }}>
      {url}
    </code>
  );
}

function ConnectionCard({ conn }: { conn: ConnectionView }) {
  const { t, locale, errorText } = useI18n();
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["connections"] });
  const act = (fn: () => Promise<unknown>) => async () => {
    setError(null);
    try {
      await fn();
      await refresh();
      void qc.invalidateQueries({ queryKey: ["events"] });
    } catch (err) {
      setError(errorText(err));
    }
  };
  const oauth = conn.provider === "google" || conn.provider === "microsoft";

  return (
    <div className="card conn-card">
      <div className="conn-head">
        <span className="prov" aria-hidden="true">
          {PROVIDER_BADGE[conn.provider]}
        </span>
        <div className="conn-who">
          <strong>{PROVIDER_NAMES[conn.provider]}</strong>
          <small className="conn-label">{conn.label}</small>
          <small>{statusLine(conn, t, locale)}</small>
        </div>
        <div className="row">
          {conn.status === "reauth" && oauth ? (
            <a className="btn btn-sm btn-primary" href={`/api/oauth/${conn.provider}/start`}>
              {t("cal.reconnect")}
            </a>
          ) : (
            <button className="btn btn-sm" disabled={conn.syncing} onClick={act(() => post(`/api/connections/${conn.id}/sync`))}>
              {t("cal.sync")}
            </button>
          )}
          <button className="btn btn-sm btn-ghost" onClick={() => setConfirming(true)}>
            {t("cal.remove")}
          </button>
        </div>
      </div>
      {error && <Alert>{error}</Alert>}
      {confirming && (
        <div className="confirm-bar">
          <span>{t("cal.removeConfirm", { label: conn.label })}</span>
          <button className="btn btn-sm btn-danger" onClick={act(() => del(`/api/connections/${conn.id}`))}>
            {t("cal.removeOk")}
          </button>
          <button className="btn btn-sm" onClick={() => setConfirming(false)}>
            {t("cal.keep")}
          </button>
        </div>
      )}
      {conn.calendars.map((cal) => (
        <CalendarRow key={cal.id} cal={cal} />
      ))}
    </div>
  );
}

function CalendarRow({ cal }: { cal: CalendarView }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [name, setName] = useState(cal.name);
  useEffect(() => setName(cal.name), [cal.name]);
  const save = async (body: CalendarPatch) => {
    // Mise à jour immédiate de l'affichage, confirmée par la réponse du serveur.
    qc.setQueryData<ConnectionView[]>(["connections"], (list) =>
      list?.map((c) => ({
        ...c,
        calendars: c.calendars.map((k) =>
          k.id === cal.id ? { ...k, ...body, name: body.displayName ?? k.name } : body.isDefault ? { ...k, isDefault: false } : k,
        ),
      })),
    );
    await patch(`/api/calendars/${cal.id}`, body).finally(() => {
      void qc.invalidateQueries({ queryKey: ["connections"] });
      void qc.invalidateQueries({ queryKey: ["events"] });
    });
  };
  const id = `cal-${cal.id}`;

  return (
    <div className="cal-row" style={{ ["--cal" as string]: cal.color }}>
      <div className="cal-name">
        <label className="swatch" title={t("cal.color", { name: cal.name })}>
          <input type="color" value={cal.color} aria-label={t("cal.color", { name: cal.name })} onChange={(e) => void save({ color: e.target.value })} />
        </label>
        <input
          className="inline-input"
          id={`${id}-name`}
          value={name}
          aria-label={t("cal.icsName")}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => name.trim() && name !== cal.name && void save({ displayName: name.trim() })}
        />
        {!cal.writable && <span className="tag">{t("cal.readOnly")}</span>}
      </div>
      <label className="switch-label">
        <input type="checkbox" checked={cal.shown} onChange={(e) => void save({ shown: e.target.checked })} />
        <span className="sw" />
        {t("cal.show")}
      </label>
      <label className="switch-label">
        <input type="checkbox" checked={cal.countsAsBusy} onChange={(e) => void save({ countsAsBusy: e.target.checked })} />
        <span className="sw" />
        {t("cal.blocks")}
      </label>
      <label className="select-label">
        {t("cal.shareDefault")}
        <select className="input input-sm" value={cal.defaultVisibility} onChange={(e) => void save({ defaultVisibility: e.target.value as CalendarView["defaultVisibility"] })}>
          {VISIBILITY_LEVELS.map((v) => (
            <option key={v} value={v}>
              {t(`vis.${v}`)}
            </option>
          ))}
        </select>
      </label>
      <label className="radio-label">
        <input type="radio" name="default-calendar" checked={cal.isDefault} disabled={!cal.writable} onChange={() => void save({ isDefault: true })} />
        {t("cal.default")}
      </label>
    </div>
  );
}

const CALDAV_PRESETS = [
  { id: "nextcloud", label: "Nextcloud", url: "https://cloud.example.com/remote.php/dav" },
  { id: "icloud", label: "iCloud", url: "https://caldav.icloud.com" },
  { id: "fastmail", label: "Fastmail", url: "https://caldav.fastmail.com/dav/" },
  { id: "other", label: "", url: "" },
];

function AddAccount({ onAdded }: { onAdded: () => void }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const providers = useQuery({ queryKey: ["providers"], queryFn: () => get<ProvidersStatus>("/api/providers") });
  const [choice, setChoice] = useState<Provider | null>(null);
  const done = () => {
    setChoice(null);
    onAdded();
    void qc.invalidateQueries({ queryKey: ["connections"] });
  };
  const options: { id: Provider; sub: string }[] = [
    { id: "google", sub: t("cal.google.sub") },
    { id: "microsoft", sub: t("cal.microsoft.sub") },
    { id: "caldav", sub: t("cal.caldav.sub") },
    { id: "ics", sub: t("cal.ics.sub") },
  ];

  return (
    <div className="card stack">
      <h3>{t("cal.add")}</h3>
      <div className="prov-grid">
        {options.map((o) => (
          <button key={o.id} className="prov-btn" aria-pressed={choice === o.id} onClick={() => setChoice(choice === o.id ? null : o.id)}>
            <strong>{o.id === "ics" ? t("cal.ics") : PROVIDER_NAMES[o.id]}</strong>
            <small>{o.sub}</small>
          </button>
        ))}
      </div>
      {(choice === "google" || choice === "microsoft") &&
        (providers.data?.[choice] ? (
          <div className="add-form">
            <p className="muted">{t("cal.redirectNote", { provider: PROVIDER_NAMES[choice] })}</p>
            <div>
              <a className="btn btn-primary" href={`/api/oauth/${choice}/start`}>
                {t("cal.continueWith", { provider: PROVIDER_NAMES[choice] })}
              </a>
            </div>
          </div>
        ) : (
          <Alert kind="info">{t("cal.notConfigured", { provider: PROVIDER_NAMES[choice] })}</Alert>
        ))}
      {choice === "caldav" && <CaldavForm onDone={done} />}
      {choice === "ics" && <IcsForm onDone={done} />}
    </div>
  );
}

function CaldavForm({ onDone }: { onDone: () => void }) {
  const { t } = useI18n();
  const [preset, setPreset] = useState("nextcloud");
  const [serverUrl, setServerUrl] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const form = useForm(() => post("/api/connections/caldav", { serverUrl, username, password }), onDone);
  const placeholder = CALDAV_PRESETS.find((p) => p.id === preset)?.url ?? "";
  return (
    <form className="add-form stack" onSubmit={form.onSubmit} noValidate>
      {form.error && <Alert>{form.error}</Alert>}
      <div className="grid2">
        <div className="field">
          <label htmlFor="cd-preset">{t("cal.service")}</label>
          <select
            id="cd-preset"
            className="input"
            value={preset}
            onChange={(e) => {
              setPreset(e.target.value);
              const p = CALDAV_PRESETS.find((x) => x.id === e.target.value);
              if (p && p.id !== "nextcloud" && p.url) setServerUrl(p.url);
            }}
          >
            {CALDAV_PRESETS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label || t("cal.other")}
              </option>
            ))}
          </select>
        </div>
        <Field label={t("cal.serverUrl")} type="url" placeholder={placeholder} value={serverUrl} onChange={(e) => setServerUrl(e.target.value)} error={form.fields.serverUrl} />
        <Field label={t("cal.username")} autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value)} error={form.fields.username} />
        <Field label={t("cal.appPassword")} type="password" autoComplete="off" value={password} onChange={(e) => setPassword(e.target.value)} error={form.fields.password} />
      </div>
      <small className="muted">{t("cal.appPasswordHint")}</small>
      <div>
        <button className="btn btn-primary" disabled={form.pending || !serverUrl || !username || !password}>
          {form.pending ? t("common.loading") : t("cal.connect")}
        </button>
      </div>
    </form>
  );
}

function IcsForm({ onDone }: { onDone: () => void }) {
  const { t } = useI18n();
  const [url, setUrl] = useState("");
  const [name, setName] = useState("");
  const form = useForm(() => post("/api/connections/ics", { url, name }), onDone);
  return (
    <form className="add-form stack" onSubmit={form.onSubmit} noValidate>
      {form.error && <Alert>{form.error}</Alert>}
      <div className="grid2">
        <Field label={t("cal.icsUrl")} type="url" placeholder="https://… .ics" value={url} onChange={(e) => setUrl(e.target.value)} error={form.fields.url} />
        <Field label={t("cal.icsName")} value={name} onChange={(e) => setName(e.target.value)} error={form.fields.name} />
      </div>
      <div>
        <button className="btn btn-primary" disabled={form.pending || !url || !name}>
          {form.pending ? t("common.loading") : t("cal.subscribe")}
        </button>
      </div>
    </form>
  );
}
